import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  limit as qLimit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getCatalog, getCatalogTrack, getMentees } from './api';
import type { Mentee } from './api';
import { weekStartIst, type DayDoc } from './people-time';
import type { ActivityItem, BoardUser, Progress, Step, Track, UserPoints, UserStreak } from '../types';

// Live Firestore reads for STAFF (direct, realtime, mobile-ready). Each hook is
// gated by being mounted only inside authorized routes (RequireAuth renders its
// children after the claim refresh), so the security rules always see the
// `audience` claim. Errors fail soft (log + empty) so one denied read never
// blanks the whole screen.

function logErr(where: string) {
  return (err: unknown) => console.warn(`[data] ${where}:`, (err as Error)?.message || err);
}

/** The signed-in user's own doc → drives the Zor pill + Kasrat streak. */
export function useMyStats(uid: string | undefined): {
  points: UserPoints | null;
  streak: UserStreak | null;
  showOnBoard: boolean;
} {
  const [points, setPoints] = useState<UserPoints | null>(null);
  const [streak, setStreak] = useState<UserStreak | null>(null);
  const [showOnBoard, setShow] = useState(true);
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const d = snap.data();
        setPoints((d?.points as UserPoints) ?? { allTime: 0, month: 0, monthKey: '' });
        setStreak((d?.streak as UserStreak) ?? { count: 0, lastActiveDate: null });
        setShow(d?.showOnBoard !== false);
      },
      logErr('useMyStats'),
    );
    return unsub;
  }, [uid]);
  return { points, streak, showOnBoard };
}

/** Published tracks, ordered. The home "your trails" list. */
export function useTracks(): { tracks: Track[]; loading: boolean } {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const q = query(collection(db, 'tracks'), where('published', '==', true), orderBy('order'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTracks(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Track, 'id'>) })));
        setLoading(false);
      },
      (err) => {
        logErr('useTracks')(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);
  return { tracks, loading };
}

/** A single track's metadata + its ordered steps. */
export function useTrackWithSteps(trackId: string | undefined): {
  track: Track | null;
  steps: Step[];
  loading: boolean;
} {
  const [track, setTrack] = useState<Track | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!trackId) return;
    const unsubT = onSnapshot(
      doc(db, 'tracks', trackId),
      (snap) => setTrack(snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<Track, 'id'>) }) : null),
      logErr('useTrack'),
    );
    const q = query(collection(db, 'tracks', trackId, 'steps'), orderBy('order'));
    const unsubS = onSnapshot(
      q,
      (snap) => {
        setSteps(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Step, 'id'>) })));
        setLoading(false);
      },
      (err) => {
        logErr('useSteps')(err);
        setLoading(false);
      },
    );
    return () => {
      unsubT();
      unsubS();
    };
  }, [trackId]);
  return { track, steps, loading };
}

/** The signed-in user's progress on a track (which steps are done + reflections).
 *  `ready` stays false until the first snapshot, so a locked exam is not guessed
 *  from an empty progress doc while the read is still in flight. */
export function useMyProgress(uid: string | undefined, trackId: string | undefined): {
  progress: Progress | null;
  ready: boolean;
} {
  const [progress, setProgress] = useState<Progress | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!uid || !trackId) {
      setProgress(null);
      setReady(false);
      return;
    }
    setProgress(null);
    setReady(false);
    const unsub = onSnapshot(
      doc(db, 'users', uid, 'progress', trackId),
      (snap) => {
        setProgress(snap.exists() ? (snap.data() as Progress) : null);
        setReady(true);
      },
      logErr('useMyProgress'),
    );
    return unsub;
  }, [uid, trackId]);
  return { progress, ready };
}

/** The Dangal leaderboard. monthKey filter → only this-month earners (no stale rows). */
export function useBoard(monthKey: string, scope: 'month' | 'allTime'): BoardUser[] {
  const [rows, setRows] = useState<BoardUser[]>([]);
  useEffect(() => {
    const q =
      scope === 'month'
        ? query(
            collection(db, 'users'),
            where('showOnBoard', '==', true),
            where('points.monthKey', '==', monthKey),
            orderBy('points.month', 'desc'),
            qLimit(50),
          )
        : query(
            collection(db, 'users'),
            where('showOnBoard', '==', true),
            orderBy('points.allTime', 'desc'),
            qLimit(50),
          );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(
          snap.docs.map((d) => {
            const data = d.data() as Partial<BoardUser> & { points?: UserPoints };
            return {
              uid: d.id,
              displayName: data.displayName || 'Someone',
              initials: data.initials || '??',
              isMentor: (data as { isMentor?: boolean }).isMentor === true,
              points: data.points || { allTime: 0, month: 0, monthKey },
            };
          }),
        );
      },
      logErr('useBoard'),
    );
    return unsub;
  }, [monthKey, scope]);
  return rows;
}

// ── API-served catalog (for CANDIDATES, who can't read tracks/steps directly) ──
// Candidates are denied direct Firestore reads by firestore.rules, so their
// catalog comes through the access-checked Functions API. Staff use the direct
// realtime hooks above; these are a one-shot fetch (tracks change rarely).

/** Granted trails for a candidate (via GET /api/catalog). Mirrors useTracks' shape. */
export function useCatalog(): { tracks: Track[]; loading: boolean } {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCatalog().then((res) => {
      if (cancelled) return;
      if (res.ok) setTracks((res.data.tracks || []).map((t) => ({ ...t })));
      else logErr('useCatalog')(res.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return { tracks, loading };
}

/** One granted track + its steps (via GET /api/track/:id). Mirrors useTrackWithSteps. */
export function useCatalogTrack(trackId: string | undefined): {
  track: Track | null;
  steps: Step[];
  loading: boolean;
} {
  const [track, setTrack] = useState<Track | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!trackId) {
      setTrack(null);
      setSteps([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCatalogTrack(trackId).then((res) => {
      if (cancelled) return;
      if (res.ok && res.data.track) {
        const { steps: s, ...t } = res.data.track;
        setTrack(t as Track);
        setSteps((s || []).slice().sort((a, b) => a.order - b.order));
      } else {
        setTrack(null);
        setSteps([]);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [trackId]);
  return { track, steps, loading };
}

/** A mentor's list of mentees (via GET /api/mentor/mentees). Staff+mentor gated server-side. */
export function useMentees(): { mentees: Mentee[]; loading: boolean } {
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMentees().then((res) => {
      if (cancelled) return;
      if (res.ok) setMentees((res.data.mentees || []) as Mentee[]);
      else logErr('useMentees')(res.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return { mentees, loading };
}

/** The shared activity feed (newest first). */
export function useActivityFeed(max = 30): ActivityItem[] {
  const [items, setItems] = useState<ActivityItem[]>([]);
  useEffect(() => {
    const q = query(collection(db, 'activity'), orderBy('createdAt', 'desc'), qLimit(max));
    const unsub = onSnapshot(
      q,
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ActivityItem, 'id'>) }))),
      logErr('useActivityFeed'),
    );
    return unsub;
  }, [max]);
  return items;
}

/** The signed-in user's own day docs for the last 7 IST days (owner read). */
export function useMyWeek(uid: string | undefined): { days: DayDoc[]; ready: boolean } {
  const [days, setDays] = useState<DayDoc[]>([]);
  const [ready, setReady] = useState(false);
  // Re-check every minute so the window moves across IST midnight (the string only changes then).
  const [weekStart, setWeekStart] = useState(() => weekStartIst(Date.now()));
  useEffect(() => {
    const id = window.setInterval(() => setWeekStart(weekStartIst(Date.now())), 60_000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (!uid) {
      setDays([]);
      setReady(false);
      return;
    }
    const q = query(collection(db, 'users', uid, 'days'), where('date', '>=', weekStart));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDays(snap.docs.map((d) => d.data() as DayDoc));
        setReady(true);
      },
      (err) => {
        // Not ready: the card hides rather than claim no Mehnat this week.
        logErr('useMyWeek')(err);
        setReady(false);
      },
    );
    return unsub;
  }, [uid, weekStart]);
  return { days, ready };
}
